/**
 * Native Web Form Questionnaire — fill / autosave / submit dialog.
 *
 * Opens from the per-case Questionnaires card on the Client Dashboard.
 * Behaviour:
 *   - Fetches `/cases/{id}/questionnaires/{key}` → definition + existing
 *     draft/submission row for the current client.
 *   - If already submitted, renders read-only view of submitted answers
 *     plus a "Download PDF" deep link to the case Documents tab.
 *   - Otherwise renders the sections + fields. Each field edit debounces
 *     into a PUT /draft so autosave never spams the API but never loses
 *     more than ~1s of work.
 *   - "Submit" calls POST /submit. Required-field errors come back in
 *     `detail.missing[]` and are surfaced as inline error pills.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import API, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import FieldRenderer from "./FieldRenderer";
import {
  CheckCircle, Clock, Warning, FloppyDisk, PaperPlaneTilt, FileText, Lock,
} from "@phosphor-icons/react";

function StatusBadge({ status, savedAt }) {
  if (status === "submitted") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full" data-testid="q-status-submitted">
        <CheckCircle weight="fill" className="w-3 h-3" /> Submitted
      </span>
    );
  }
  if (status === "saving") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full" data-testid="q-status-saving">
        <FloppyDisk weight="bold" className="w-3 h-3 animate-pulse" /> Saving…
      </span>
    );
  }
  if (status === "saved") {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-medium bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full" data-testid="q-status-saved">
        <CheckCircle weight="bold" className="w-3 h-3" /> Saved {savedAt ? `· ${new Date(savedAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}` : ""}
      </span>
    );
  }
  return null;
}

export default function QuestionnaireForm({ open, onOpenChange, caseId, qKey, onSubmitted }) {
  const [loading, setLoading] = useState(true);
  const [definition, setDefinition] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [answers, setAnswers] = useState({});
  const [missing, setMissing] = useState([]);
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const answersRef = useRef(answers);
  const dirtyRef = useRef(false);
  const mountedRef = useRef(true);
  const isSubmitted = submission?.status === "submitted";

  useEffect(() => { answersRef.current = answers; }, [answers]);
  useEffect(() => () => { mountedRef.current = false; }, []);

  useEffect(() => {
    if (!open || !qKey) return;
    let cancelled = false;
    setLoading(true);
    setMissing([]);
    API.get(`/cases/${caseId}/questionnaires/${qKey}`)
      .then(({ data }) => {
        if (cancelled) return;
        setDefinition(data.definition);
        setSubmission(data.submission);
        setAnswers(data.submission?.answers || {});
        setLastSavedAt(data.submission?.last_saved_at || null);
      })
      .catch((err) => {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load questionnaire");
        onOpenChange(false);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // Intentionally exclude onOpenChange: the parent recreates it on every
    // render (incl. its 8s poll), and including it here re-runs the fetch
    // mid-typing, wiping unsaved answers. Only re-fetch when the target
    // questionnaire actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, qKey, caseId]);

  // Save-on-exit only — never while typing. We persist the draft when
  // the client closes the questionnaire, switches away (tab hidden /
  // page hide) or the component unmounts. This stops the screen from
  // jumping mid-answer that the per-keystroke autosave caused.
  const saveDraft = useCallback(async () => {
    if (isSubmitted || !dirtyRef.current) return;
    dirtyRef.current = false;
    try {
      if (mountedRef.current) setSaveStatus("saving");
      const { data } = await API.put(`/cases/${caseId}/questionnaires/${qKey}/draft`, { answers: answersRef.current });
      if (mountedRef.current) { setLastSavedAt(data.last_saved_at); setSaveStatus("saved"); }
    } catch (err) {
      dirtyRef.current = true; // let a later exit retry
      if (mountedRef.current) setSaveStatus("idle");
      const detail = err.response?.data?.detail;
      if (typeof detail === "string") toast.error(detail);
    }
  }, [caseId, qKey, isSubmitted]);

  useEffect(() => {
    if (!open || isSubmitted) return;
    const onHide = () => { if (document.hidden) saveDraft(); };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", saveDraft);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", saveDraft);
      saveDraft(); // save when the dialog/component goes away
    };
  }, [open, isSubmitted, saveDraft]);

  const setField = (key, value) => {
    setAnswers((a) => ({ ...a, [key]: value }));
    dirtyRef.current = true;
    // Clear matching error pill once user updates a missing field
    if (missing.length) {
      setMissing((m) => m.filter((entry) => !entry.toLowerCase().includes(key.replace(/_/g, " "))));
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setMissing([]);
    try {
      const { data } = await API.post(`/cases/${caseId}/questionnaires/${qKey}/submit`, { answers });
      toast.success(`${definition.label} submitted — your solicitor has been notified.`);
      setSubmission({
        status: "submitted",
        submitted_at: data.submitted_at,
        submitted_pdf_document_id: data.document_id,
        answers,
      });
      onSubmitted?.(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && Array.isArray(detail.missing)) {
        setMissing(detail.missing);
        toast.error(`Please complete ${detail.missing.length} required answer${detail.missing.length === 1 ? "" : "s"} before submitting.`);
      } else {
        toast.error(formatApiErrorDetail(detail) || "Failed to submit");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const totalRequired = useMemo(() => {
    if (!definition) return 0;
    let n = 0;
    for (const s of definition.sections) {
      for (const f of s.fields) if (f.required) n += 1;
    }
    return n;
  }, [definition]);

  const completedRequired = useMemo(() => {
    if (!definition) return 0;
    let n = 0;
    for (const s of definition.sections) {
      for (const f of s.fields) {
        if (!f.required) continue;
        const gate = f.show_if;
        if (gate && answers[gate.key] !== gate.equals) { n += 1; continue; } // hidden = counts as met
        const v = answers[f.key];
        if (f.type === "checkbox") { if (v === true) n += 1; continue; }
        if (f.type === "multiselect") { if (Array.isArray(v) && v.length) n += 1; continue; }
        if (f.type === "address") { if (v && v.line1 && v.postcode) n += 1; continue; }
        if (v !== undefined && v !== null && String(v).trim() !== "") n += 1;
      }
    }
    return n;
  }, [definition, answers]);

  const progressPct = totalRequired === 0 ? 0 : Math.round((completedRequired / totalRequired) * 100);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="questionnaire-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }} className="flex items-center gap-2 flex-wrap">
            {definition?.label || "Questionnaire"}
            <StatusBadge status={isSubmitted ? "submitted" : saveStatus} savedAt={lastSavedAt} />
            {definition?.estimated_minutes && !isSubmitted && (
              <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 font-normal ml-auto">
                <Clock weight="bold" className="w-3 h-3" /> ~{definition.estimated_minutes} min
              </span>
            )}
          </DialogTitle>
          <DialogDescription>{definition?.description}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-sm text-slate-500">Loading…</div>
        ) : (
          <>
            {isSubmitted && (
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-4 py-3 mb-3 flex items-start gap-3" data-testid="q-submitted-banner">
                <Lock weight="bold" className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
                <div className="text-sm text-emerald-900">
                  <strong>Submitted{submission?.submitted_at ? ` on ${new Date(submission.submitted_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}` : ""}.</strong>{" "}
                  Your answers are locked. Contact your solicitor if you need to amend anything — they can reopen the form for you.
                </div>
              </div>
            )}

            {!isSubmitted && totalRequired > 0 && (
              <div className="mb-3" data-testid="q-progress">
                <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1">
                  <span>Required fields complete</span>
                  <span><strong className="text-slate-700">{completedRequired}</strong> / {totalRequired}</span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#1462a0] transition-all"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </div>
            )}

            {missing.length > 0 && (
              <div className="rounded-lg bg-rose-50 border border-rose-200 px-4 py-3 mb-3" data-testid="q-missing-banner">
                <div className="flex items-center gap-2 text-sm font-semibold text-rose-900 mb-1">
                  <Warning weight="fill" className="w-4 h-4" />
                  {missing.length} required answer{missing.length === 1 ? "" : "s"} missing
                </div>
                <ul className="text-xs text-rose-800 list-disc list-inside space-y-0.5">
                  {missing.map((m) => <li key={m}>{m}</li>)}
                </ul>
              </div>
            )}

            <div className="space-y-6">
              {(definition?.sections || []).map((section, sIdx) => (
                <section key={section.title} data-testid={`q-section-${sIdx}`}>
                  <h3 className="text-sm font-bold text-slate-900 border-b border-slate-200 pb-1.5 mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {section.title}
                  </h3>
                  <div className="space-y-3">
                    {section.fields.map((f) => (
                      <FieldRenderer
                        key={f.key}
                        field={f}
                        value={answers[f.key]}
                        answers={answers}
                        onChange={setField}
                        readOnly={isSubmitted}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </>
        )}

        <DialogFooter className="border-t border-slate-100 pt-3 mt-2 flex-col-reverse sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="q-close">
            {isSubmitted ? "Close" : "Close — answers are saved"}
          </Button>
          {!isSubmitted && (
            <Button
              onClick={handleSubmit}
              disabled={submitting || loading}
              className="bg-[#1462a0] hover:bg-[#10507f] text-white"
              data-testid="q-submit"
            >
              <PaperPlaneTilt weight="bold" className="w-4 h-4 mr-1" />
              {submitting ? "Submitting…" : "Submit to solicitor"}
            </Button>
          )}
          {isSubmitted && submission?.submitted_pdf_document_id && (
            <span className="text-xs text-slate-500 self-center inline-flex items-center gap-1">
              <FileText weight="bold" className="w-3.5 h-3.5" />
              A PDF has been filed in your case documents.
            </span>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
